// src/components/Registration/steps/Step1Identity.jsx
import React, { useEffect, useRef, useState } from 'react';

const PEACH = '#EBA477';

// Reusable frosted input (dark glass w/ white text)
const FrostedInput = ({ style, ...props }) => (
  <input
    className="frosted-input"
    {...props}
    style={{
      width: '100%',
      height: 60,
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 18,
      padding: '0 18px',
      outline: 'none',
      boxSizing: 'border-box',
      maxWidth: '100%',
      ...style,
    }}
  />
);

// Small two-pill toggle (Admin / Owner)
function TogglePills({ value, onChange, options, style }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        gap: 12,
        ...style,
      }}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              height: 56,
              borderRadius: 16,
              background: 'rgba(255,255,255,0.10)',
              border: selected ? `2px solid ${PEACH}` : '1px solid rgba(255,255,255,0.10)',
              color: 'white',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              transition: 'transform 120ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Step1Identity({
  data = {},
  onNext,
  onPrevious,
  loading,
  isFirstStep,
  onBindNext,
  requirePassword = true,
}) {
  const [fullName, setFullName] = useState(data.fullName || '');
  const [email, setEmail] = useState(data.email || '');
  const [password, setPassword] = useState(data.password || '');
  const [phone, setPhone] = useState(data.phone || '');
  const [role, setRole] = useState(data.role || 'admin'); // 'admin' | 'owner'

  // Keep latest onNext without making the effect depend on it
  const onNextRef = useRef(onNext);
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  // Normalizers/validators (match backend expectations)
  const normalizeEmail = (x) => String(x || '').trim().toLowerCase();
  const normalizePhone = (x) => String(x || '').replace(/\D/g, '');
  const validEmail = /\S+@\S+\.\S+/.test(normalizeEmail(email));
  const validPhone = normalizePhone(phone).length >= 10;
  const validPassword = !requirePassword || (password || '').length >= 8;
  const canContinue = fullName.trim().length > 1 && validEmail && validPassword && validPhone;

  // Bind the parent chevron – IMPORTANT: do not depend on onNext (use the ref)
  useEffect(() => {
    const handleNext = () => {
      if (!canContinue || loading) return;
      onNextRef.current?.({
        ...data,
        fullName: fullName.trim(),
        email: normalizeEmail(email),
        phone: normalizePhone(phone),
        role: role === 'owner' ? 'owner' : 'admin',
        ...(requirePassword ? { password } : {}),
      });
    };

    onBindNext?.({
      onClick: handleNext,
      disabled: !canContinue || loading,
    });

    return () => onBindNext?.({ onClick: null, disabled: true });
    // deps: only values that should re-bind the button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullName, email, password, phone, role, loading, canContinue, onBindNext, data]);

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (!canContinue || loading) return;
    onNextRef.current?.({
      ...data,
      fullName: fullName.trim(),
      email: normalizeEmail(email),
      phone: normalizePhone(phone),
      role: role === 'owner' ? 'owner' : 'admin',
      ...(requirePassword ? { password } : {}),
    });
  };

  return (
    <form style={{ color: 'white' }} onSubmit={handleSubmitForm} autoComplete="on">
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Account Setup</h2>
      <p style={{ marginTop: 0, opacity: 0.9 }}>Create your account to continue.</p>

      {/* Full Name */}
      <label style={{ display: 'block', marginTop: 8, marginBottom: 6, opacity: 0.9 }}>
        Your Full Name *
      </label>
      <FrostedInput
        autoComplete="name"
        placeholder="e.g., Jane Doe"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
      />

      {/* Email */}
      <label style={{ display: 'block', marginTop: 14, marginBottom: 6, opacity: 0.9 }}>
        Your Email *
      </label>
      <FrostedInput
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      {requirePassword && (
        <>
          <label style={{ display: 'block', marginTop: 14, marginBottom: 6, opacity: 0.9 }}>
            Password *
          </label>
          <FrostedInput
            type="password"
            autoComplete="new-password"
            placeholder="8+ characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </>
      )}

      {/* Phone for 2FA */}
      <label style={{ display: 'block', marginTop: 14, marginBottom: 6, opacity: 0.9 }}>
        Phone *
      </label>
      <FrostedInput
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="(555) 123-4567"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
      />

      {/* Role selection */}
      <label style={{ display: 'block', marginTop: 16, marginBottom: 6, opacity: 0.9 }}>
        Your Role *
      </label>
      <TogglePills
        value={role}
        onChange={setRole}
        options={[
          { value: 'admin', label: 'Business Admin' },
          { value: 'owner', label: 'Business Owner' },
        ]}
      />

      {!isFirstStep && (
        <button
          type="button"
          onClick={onPrevious}
          style={{
            marginTop: 16,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.9)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      )}

      <style>{`
        .frosted-input::placeholder { color: rgba(255,255,255,0.75); }
      `}</style>
    </form>
  );
}
